import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiGet, apiPost } from "../../../services/api";
import { useToast } from "../../common/ToastProvider";
import Button from "../../common/Button";
import PerioChart, { type PerioRow } from "./PerioChart";

type PerioData = Record<string, PerioRow>;
type BpeSextant = "UR" | "UA" | "UL" | "LL" | "LA" | "LR";
type BpeData = {
  sextants: Record<BpeSextant, number | null>;
  furcation: Record<BpeSextant, boolean>;
  notes?: string;
};
type PerioPayload = { chart?: PerioData; bpe?: BpeData } | PerioData;
type PerioHistoryRow = { visit_id: number; visit_date?: string; updated_at?: string | null; data?: PerioPayload };

const BPE_SEXTANTS: { id: BpeSextant; label: string; teeth: string[] }[] = [
  { id: "UR", label: "Upper Right", teeth: ["17", "16", "15", "14"] },
  { id: "UA", label: "Upper Anterior", teeth: ["13", "12", "11", "21", "22", "23"] },
  { id: "UL", label: "Upper Left", teeth: ["24", "25", "26", "27"] },
  { id: "LL", label: "Lower Left", teeth: ["37", "36", "35", "34"] },
  { id: "LA", label: "Lower Anterior", teeth: ["33", "32", "31", "41", "42", "43"] },
  { id: "LR", label: "Lower Right", teeth: ["44", "45", "46", "47"] },
];

const emptyBpe = (): BpeData => ({
  sextants: { UR: null, UA: null, UL: null, LL: null, LA: null, LR: null },
  furcation: { UR: false, UA: false, UL: false, LL: false, LA: false, LR: false },
  notes: "",
});

const normalizePayload = (raw: unknown): { chart: PerioData; bpe: BpeData } => {
  const freshBpe = emptyBpe();
  if (!raw || typeof raw !== "object") return { chart: {}, bpe: freshBpe };
  const obj = raw as Record<string, unknown>;
  if ("chart" in obj) {
    const chart = (obj.chart && typeof obj.chart === "object") ? (obj.chart as PerioData) : {};
    const bpeRaw = (obj.bpe && typeof obj.bpe === "object") ? (obj.bpe as Partial<BpeData>) : {};
    return {
      chart,
      bpe: {
        ...freshBpe,
        ...bpeRaw,
        sextants: { ...freshBpe.sextants, ...(bpeRaw.sextants || {}) },
        furcation: { ...freshBpe.furcation, ...(bpeRaw.furcation || {}) },
      },
    };
  }
  // Legacy payload: data is directly the chart
  return { chart: obj as PerioData, bpe: freshBpe };
};

const calcBpeFromChart = (chart: PerioData): BpeData => {
  const next = emptyBpe();
  BPE_SEXTANTS.forEach((s) => {
    let maxDepth: number | null = null;
    s.teeth.forEach((tooth) => {
      const row = chart[tooth];
      if (!row) return;
      ["MB", "B", "DB", "DL", "L", "ML"].forEach((site) => {
        const val = row[site];
        if (val == null || Number.isNaN(Number(val))) return;
        const n = Number(val);
        if (maxDepth == null || n > maxDepth) maxDepth = n;
      });
    });
    if (maxDepth == null) {
      next.sextants[s.id] = null;
    } else if (maxDepth >= 6) {
      next.sextants[s.id] = 4;
    } else if (maxDepth >= 4) {
      next.sextants[s.id] = 3;
    } else {
      next.sextants[s.id] = 0;
    }
  });
  return next;
};

export default function PerioConsole({ visitId, readOnly }: { visitId: string | number; readOnly?: boolean }) {
  const vid = String(visitId);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [chartData, setChartData] = useState<PerioData>({});
  const [bpeData, setBpeData] = useState<BpeData>(emptyBpe());
  const [dirty, setDirty] = useState(false);
  const saveTimerRef = useRef<number | null>(null);

  const [patientId, setPatientId] = useState<string | null>(null);
  const [history, setHistory] = useState<PerioHistoryRow[]>([]);
  const [compareVisitId, setCompareVisitId] = useState<string>("");
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareChartData, setCompareChartData] = useState<PerioData>({});
  const [compareBpeData, setCompareBpeData] = useState<BpeData>(emptyBpe());
  const [compareUpdatedAt, setCompareUpdatedAt] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setDirty(false);
    try {
      const res = (await apiGet(`/api/doctor/perio/${encodeURIComponent(vid)}`)) as {
        data?: PerioPayload;
        updated_at?: string | null;
      };
      const parsed = normalizePayload(res?.data);
      setChartData(parsed.chart);
      setBpeData(parsed.bpe);
      setUpdatedAt(res?.updated_at ?? null);
    } catch (e: unknown) {
      setChartData({});
      setBpeData(emptyBpe());
      setUpdatedAt(null);
      showToast({ message: "Failed to load periodontal chart.", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [vid]);

  const loadPatient = useCallback(async () => {
    try {
      const res = (await apiGet(`/api/doctor/visit/${encodeURIComponent(vid)}`)) as { patient?: { patient_id?: string | number; mobile?: string | number } };
      const pid = res?.patient?.patient_id ?? res?.patient?.mobile;
      setPatientId(pid != null ? String(pid) : null);
    } catch {
      setPatientId(null);
    }
  }, [vid]);

  const loadHistory = useCallback(async (pid: string) => {
    try {
      const res = (await apiGet(`/api/doctor/perio/history/${encodeURIComponent(pid)}?limit=12`)) as PerioHistoryRow[];
      const list = Array.isArray(res) ? res : [];
      setHistory(list.filter((h) => String(h.visit_id) !== String(vid)));
    } catch {
      setHistory([]);
    }
  }, [vid]);

  const loadCompare = useCallback(async (otherVisitId: string) => {
    if (!otherVisitId) return;
    setCompareLoading(true);
    try {
      const res = (await apiGet(`/api/doctor/perio/${encodeURIComponent(otherVisitId)}`)) as { data?: PerioPayload; updated_at?: string | null };
      const parsed = normalizePayload(res?.data);
      setCompareChartData(parsed.chart);
      setCompareBpeData(parsed.bpe);
      setCompareUpdatedAt(res?.updated_at ?? null);
    } catch (e: unknown) {
      setCompareChartData({});
      setCompareBpeData(emptyBpe());
      setCompareUpdatedAt(null);
      showToast({ message: "Failed to load comparison perio chart.", type: "error" });
    } finally {
      setCompareLoading(false);
    }
  }, []);

  const save = useCallback(async (chartPayload: PerioData, bpePayload: BpeData) => {
    if (readOnly) return;
    setSaving(true);
    try {
      const res = (await apiPost("/api/doctor/perio", {
        visit_id: Number(vid),
        data: { chart: chartPayload, bpe: bpePayload },
      })) as { updated_at?: string | null };
      setUpdatedAt(res?.updated_at ?? null);
      setDirty(false);
      showToast({ message: "Perio chart saved successfully.", type: "success" });
    } catch (e: unknown) {
      showToast({ message: "Failed to save perio chart.", type: "error" });
    } finally {
      setSaving(false);
    }
  }, [vid, readOnly]);

  useEffect(() => {
    void load();
    void loadPatient();
    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, [load, loadPatient]);

  useEffect(() => {
    if (!patientId) {
      setHistory([]);
      return;
    }
    void loadHistory(patientId);
  }, [patientId, loadHistory]);

  const statusInfo = useMemo(() => {
    if (loading) return { text: "Loading…", cls: "loading" };
    if (saving) return { text: "Saving…", cls: "loading" };
    if (dirty) return { text: "Unsaved changes", cls: "dirty" };
    if (updatedAt) return { text: `Saved ${new Date(updatedAt).toLocaleString()}`, cls: "saved" };
    return { text: "No data yet", cls: "loading" };
  }, [loading, saving, dirty, updatedAt]);

  const handleChange = (next: PerioData) => {
    setChartData(next);
    setDirty(true);
    if (readOnly) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      void save(next, bpeData);
    }, 900);
  };

  const handleBpeChange = (next: BpeData) => {
    setBpeData(next);
    setDirty(true);
    if (readOnly) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      void save(chartData, next);
    }, 900);
  };

  return (
    <div className="mod-standalone-window perio-shell">
      
      {/* Glossy Header */}
      <div className="perio-header-bar">
        <div className="perio-header-left">
          <h3 className="perio-header-title">
            🩸 Periodontal Chart
            {readOnly && <span className="perio-locked-pill">Locked</span>}
          </h3>
          <p className="perio-header-sub">
            {updatedAt ? `Last saved: ${new Date(updatedAt).toLocaleTimeString()}` : "No saved data yet"}
          </p>
        </div>

        <div className="perio-header-actions">
          {history.length > 0 && (
            <div className="perio-compare-row">
              <label className="perio-compare-label">Compare:</label>
              <select
                value={compareVisitId}
                onChange={(e) => {
                  const next = e.target.value;
                  setCompareVisitId(next);
                  setCompareChartData({});
                  setCompareBpeData(emptyBpe());
                  setCompareUpdatedAt(null);
                  if (next) void loadCompare(next);
                }}
                disabled={compareLoading}
                className="perio-compare-select"
              >
                <option value="">None</option>
                {history.map((h) => (
                  <option key={h.visit_id} value={String(h.visit_id)}>
                    Visit #{h.visit_id} {h.visit_date ? `(${new Date(h.visit_date).toLocaleDateString()})` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="perio-header-buttons">
            <Button variant="ghost" onClick={() => void load()} disabled={loading || saving}>Refresh</Button>
            <Button variant="primary" onClick={() => void save(chartData, bpeData)} isLoading={saving} disabled={readOnly || !dirty}>
              Save Chart
            </Button>
          </div>
        </div>
      </div>

      {/* Chart Content Area */}
      <div className="perio-content">
        <div className={`perio-grid ${compareVisitId ? "two-col" : ""}`}>
          
          <div className="perio-panel">
            <div className="perio-bpe-panel">
              <div className="perio-bpe-header">
                <div>
                  <strong>BPE (Basic Periodontal Exam)</strong>
                  <div className="perio-bpe-sub">Quick sextant scores + furcation</div>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    const auto = calcBpeFromChart(chartData);
                    handleBpeChange({
                      ...auto,
                      notes: bpeData.notes,
                      furcation: { ...bpeData.furcation },
                    });
                  }}
                  disabled={Boolean(readOnly)}
                >
                  Auto from depths
                </Button>
              </div>
              <div className="perio-bpe-grid">
                {BPE_SEXTANTS.map((s) => (
                  <div key={s.id} className="perio-bpe-card">
                    <div className="perio-bpe-label">{s.label}</div>
                    <div className="perio-bpe-teeth">{s.teeth.join(", ")}</div>
                    <div className="perio-bpe-controls">
                      <select
                        value={bpeData.sextants[s.id] ?? ""}
                        onChange={(e) => {
                          const val = e.target.value === "" ? null : Number(e.target.value);
                          handleBpeChange({
                            ...bpeData,
                            sextants: { ...bpeData.sextants, [s.id]: val },
                          });
                        }}
                        disabled={Boolean(readOnly)}
                      >
                        <option value="">—</option>
                        <option value={0}>0</option>
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                        <option value={4}>4</option>
                      </select>
                      <label className="perio-bpe-star">
                        <input
                          type="checkbox"
                          checked={Boolean(bpeData.furcation[s.id])}
                          onChange={(e) =>
                            handleBpeChange({
                              ...bpeData,
                              furcation: { ...bpeData.furcation, [s.id]: e.target.checked },
                            })
                          }
                          disabled={Boolean(readOnly)}
                        />
                        *
                      </label>
                    </div>
                  </div>
                ))}
              </div>
              <textarea
                className="perio-bpe-notes"
                placeholder="BPE notes (optional)"
                value={bpeData.notes || ""}
                onChange={(e) => handleBpeChange({ ...bpeData, notes: e.target.value })}
                disabled={Boolean(readOnly)}
              />
            </div>

            <PerioChart value={chartData} onChange={handleChange} readOnly={Boolean(readOnly)} />
          </div>

          {compareVisitId && (
            <div className="perio-panel">
              <div className="perio-compare-title">
                Historical Comparison
              </div>
              <div className="perio-bpe-panel compare">
                <div className="perio-bpe-header">
                  <strong>BPE Summary</strong>
                </div>
                <div className="perio-bpe-grid">
                  {BPE_SEXTANTS.map((s) => (
                    <div key={s.id} className="perio-bpe-card">
                      <div className="perio-bpe-label">{s.label}</div>
                      <div className="perio-bpe-teeth">{s.teeth.join(", ")}</div>
                      <div className="perio-bpe-controls">
                        <select value={compareBpeData.sextants[s.id] ?? ""} disabled>
                          <option value="">—</option>
                          <option value={0}>0</option>
                          <option value={1}>1</option>
                          <option value={2}>2</option>
                          <option value={3}>3</option>
                          <option value={4}>4</option>
                        </select>
                        <label className="perio-bpe-star muted">
                          <input type="checkbox" checked={Boolean(compareBpeData.furcation[s.id])} readOnly />
                          *
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
                {compareBpeData.notes && (
                  <div className="perio-bpe-notes readonly">{compareBpeData.notes}</div>
                )}
              </div>
              <PerioChart value={compareChartData} readOnly />
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
